import { useState, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Popover,
  Stack,
  Switch,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import StarIcon from "@mui/icons-material/Star";
import { getUserGeneratedHighlightBg } from "../reporting-constants";

interface ReportSettingsItem {
  id: string;
  title: string;
  type?: "admin" | "user_generated";
}

interface ReportSettingsPopoverProps {
  anchor: HTMLElement | null;
  onClose: () => void;
  reports: ReportSettingsItem[];
  hiddenReports: string[];
  onSave: (payload: { hiddenReports: string[]; orderedReportIds: string[] }) => void;
  saving?: boolean;
  onCreateNew?: () => void;
  onDeleteReport: (id: string) => void;
  onRestructureReport: (id: string) => void;
}

export const ReportSettingsPopover = ({
  anchor,
  onClose,
  reports,
  hiddenReports,
  onSave,
  saving = false,
  onCreateNew,
  onDeleteReport,
  onRestructureReport,
}: ReportSettingsPopoverProps) => {
  const [draftHidden, setDraftHidden] = useState<string[]>(hiddenReports);
  const [draftOrdered, setDraftOrdered] = useState<ReportSettingsItem[]>(reports);

  // Sync draft when the popover opens
  useEffect(() => {
    if (anchor) {
      setDraftHidden(hiddenReports);
      setDraftOrdered(reports);
    }
  }, [anchor, hiddenReports, reports]);

  const visibleCount = draftOrdered.length - draftHidden.length;

  const handleToggle = (id: string) => {
    const isHidden = draftHidden.includes(id);
    if (isHidden) {
      setDraftHidden((prev) => prev.filter((r) => r !== id));
    } else {
      if (visibleCount <= 1) return;
      setDraftHidden((prev) => [...prev, id]);
    }
  };

  const moveReport = (index: number, direction: -1 | 1) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= draftOrdered.length) return;
    
    setDraftOrdered((prev) => {
      const reordered = [...prev];
      const [moved] = reordered.splice(index, 1);
      reordered.splice(nextIndex, 0, moved);
      return reordered;
    });
  };

  const handleSave = () => {
    onSave({
      hiddenReports: draftHidden,
      orderedReportIds: draftOrdered.map((r) => r.id),
    });
  };

  const handleClose = () => {
    setDraftHidden(hiddenReports);
    setDraftOrdered(reports);
    onClose();
  };

  return (
    <Popover
      open={!!anchor}
      anchorEl={anchor}
      onClose={handleClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      sx={{ "& .MuiPaper-root": { p: 1.5, minWidth: 300 } }}
    >
      <Typography variant="subtitle2" sx={{ px: 1, pb: 1, fontWeight: 600 }}>
        Show / Hide Reports
      </Typography>
      <Divider sx={{ mb: 0.5 }} />

      {draftOrdered.map((report, index) => {
        const isUserGenerated = report.type === "user_generated";
        const isVisible = !draftHidden.includes(report.id);
        const isLastVisible = isVisible && visibleCount <= 1;
        const canMoveUp = isUserGenerated && index > 0;
        const canMoveDown = isUserGenerated && index < draftOrdered.length - 1;

        return (
          <Box key={report.id}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{
                width: "100%",
                px: 1,
                py: 0.75,
                ...(isUserGenerated && {
                  bgcolor: (theme: Theme) => getUserGeneratedHighlightBg(theme),
                  borderLeft: "4px solid",
                  borderLeftColor: "info.main",
                }),
              }}
            >
              {/* Star + title */}
              <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 0.5 }}>
                {isUserGenerated && (
                  <StarIcon fontSize="small" sx={{ color: "info.main", flexShrink: 0 }} />
                )}
                <Typography
                  variant="body2"
                  noWrap
                  sx={{ fontWeight: isUserGenerated ? 600 : 500, color: isVisible ? "text.primary" : "text.disabled" }}
                >
                  {report.title}
                </Typography>
              </Box>

              {/* Move up/down — only for user-generated reports */}
              {isUserGenerated && (
                <>
                  <Tooltip title="Move up">
                    <span>
                      <IconButton size="small" disabled={!canMoveUp} onClick={() => moveReport(index, -1)}>
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Move down">
                    <span>
                      <IconButton size="small" disabled={!canMoveDown} onClick={() => moveReport(index, 1)}>
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              )}

              {/* Delete — only for user-generated reports */}
              {isUserGenerated && (
                <Tooltip title="Delete report">
                  <IconButton
                    size="small"
                    onClick={() => { handleClose(); onDeleteReport(report.id); }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {/* Restructure — only for user-generated reports */}
              {isUserGenerated && (
                <Tooltip title="Change data source (removes all charts)">
                  <IconButton
                    size="small"
                    onClick={() => { handleClose(); onRestructureReport(report.id); }}
                    sx={{ color: "warning.main" }}
                  >
                    <span style={{ fontSize: 14, fontWeight: 700, lineHeight: 1 }}>⚙</span>
                  </IconButton>
                </Tooltip>
              )}

              <Switch
                checked={isVisible}
                onChange={() => handleToggle(report.id)}
                disabled={isLastVisible}
                color="primary"
                size="small"
              />
            </Stack>
            <Divider />
          </Box>
        );
      })}

      <Box sx={{ pt: 1.5, display: "flex", width: "100%", gap: 1 }}>
        <Button size="small" variant="outlined" onClick={handleClose} disabled={saving} sx={{ flex: 1 }}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{ flex: 1 }}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </Box>

      {onCreateNew && (
        <>
          <Divider sx={{ my: 1 }} />
          <Button
            fullWidth
            size="small"
            variant="contained"
            color="success"
            sx={{ mt: 0.5, py: 1 }}
            onClick={() => {
              handleClose();
              onCreateNew();
            }}
          >
            + Create New Report
          </Button>
        </>
      )}
    </Popover>
  );
};
